import React, { useState, useEffect, useCallback } from 'react';
import {
    Drawer, Box, Typography, Tabs, Tab, IconButton, Divider,
    Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
    Button, TextField, Select, MenuItem, FormControl, InputLabel,
    Chip, Alert, CircularProgress, Tooltip, Switch, FormControlLabel,
    Paper, Avatar,
} from '@mui/material';
import { getBaseDomain } from '../api';

import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StoreIcon from '@mui/icons-material/Store';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SaveIcon from '@mui/icons-material/Save';

const getApiBase = () => {
    const protocol = process.env.REACT_APP_API_PROTOCOL || 'http';
    const port = process.env.REACT_APP_API_PORT ? `:${process.env.REACT_APP_API_PORT}` : '';
    return `${protocol}://${window.location.hostname}${port}/api/admin`;
};

// ─── Reusable API helper ──────────────────────────────────────────────────────
const api = (path, opts = {}) =>
    fetch(`${getApiBase()}${path}`, {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'include',
        ...opts,
        body: opts.body ? JSON.stringify(opts.body) : undefined,
    }).then(r => r.json());


// ─── Empty form states ────────────────────────────────────────────────────────
const emptyBranch = { name: '', address: '', contact_no: '', is_main: false };
const emptyUser   = { name: '', email: '', password: '', role: 'user', branch_id: '' };

// ═══════════════════════════════════════════════════════════════════════════════
// BRANCHES TAB
// ═══════════════════════════════════════════════════════════════════════════════
const BranchesTab = ({ tenantId }) => {
    const [branches, setBranches]   = useState([]);
    const [loading, setLoading]     = useState(true);
    const [saving, setSaving]       = useState(false);
    const [form, setForm]           = useState(emptyBranch);
    const [editId, setEditId]       = useState(null); // null = add mode
    const [error, setError]         = useState('');
    const [success, setSuccess]     = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        const data = await api(`/tenants/${tenantId}/branches`);
        setBranches(Array.isArray(data) ? data : []);
        setLoading(false);
    }, [tenantId]);

    useEffect(() => { load(); }, [load]);

    const startEdit = (b) => {
        setEditId(b.id);
        setForm({ name: b.name, address: b.address || '', contact_no: b.contact_no || '', is_main: b.is_main });
        setError(''); setSuccess('');
    };

    const cancelEdit = () => { setEditId(null); setForm(emptyBranch); setError(''); };

    const handleSave = async () => {
        if (!form.name.trim()) { setError('Branch name आवश्यक आहे.'); return; }
        setSaving(true); setError(''); setSuccess('');
        try {
            if (editId) {
                await api(`/tenants/${tenantId}/branches/${editId}`, { method: 'PUT', body: form });
                setSuccess('Branch update झाली!');
            } else {
                await api(`/tenants/${tenantId}/branches`, { method: 'POST', body: form });
                setSuccess('नवीन Branch तयार झाली!');
            }
            setEditId(null); setForm(emptyBranch);
            await load();
        } catch {
            setError('काहीतरी चुकले. पुन्हा प्रयत्न करा.');
        }
        setSaving(false);
    };

    const handleDelete = async (b) => {
        if (b.is_main) { setError('Head Office branch delete करता येत नाही.'); return; }
        if (!window.confirm(`"${b.name}" branch delete करायची?`)) return;
        setError(''); setSuccess('');
        const res = await api(`/tenants/${tenantId}/branches/${b.id}`, { method: 'DELETE' });
        if (res.error) { setError(res.error); } else { setSuccess('Branch delete झाली.'); await load(); }
    };

    return (
        <Box>
            {/* Branches Table */}
            {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Branch Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Address</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: 90 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {branches.length === 0 && (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: '#888' }}>कोणतीही Branch नाही.</TableCell></TableRow>
                            )}
                            {branches.map(b => (
                                <TableRow key={b.id} hover selected={editId === b.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {b.is_main
                                                ? <AccountBalanceIcon fontSize="small" sx={{ color: '#FFB300' }} />
                                                : <StoreIcon fontSize="small" sx={{ color: '#0FA958' }} />}
                                            <Typography variant="body2" fontWeight={b.is_main ? 'bold' : 'normal'}>
                                                {b.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell><Typography variant="caption" color="textSecondary">{b.address || '—'}</Typography></TableCell>
                                    <TableCell><Typography variant="caption" color="textSecondary">{b.contact_no || '—'}</Typography></TableCell>
                                    <TableCell>
                                        {b.is_main
                                            ? <Chip label="Head Office" size="small" sx={{ bgcolor: '#FFF8E1', color: '#F57F17', fontWeight: 'bold', fontSize: '0.7rem' }} />
                                            : <Chip label="Branch" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Edit"><IconButton size="small" onClick={() => startEdit(b)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        {!b.is_main && (
                                            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(b)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Alerts */}
            {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Add / Edit Form */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {editId ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                    {editId ? 'Branch Edit करा' : 'नवीन Branch जोडा'}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                        label="Branch Name *" size="small" fullWidth
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    />
                    <TextField
                        label="Contact No." size="small" fullWidth
                        value={form.contact_no}
                        onChange={e => setForm(f => ({ ...f, contact_no: e.target.value }))}
                    />
                    <TextField
                        label="Address" size="small" fullWidth
                        sx={{ gridColumn: '1 / -1' }}
                        value={form.address}
                        onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.is_main}
                                onChange={e => setForm(f => ({ ...f, is_main: e.target.checked }))}
                                color="warning"
                            />
                        }
                        label="Head Office म्हणून सेट करा"
                    />
                </Box>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained" size="small"
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave} disabled={saving}
                        sx={{ bgcolor: '#1B5E20', '&:hover': { bgcolor: '#0C3D12' } }}
                    >
                        {editId ? 'Update करा' : 'Branch जोडा'}
                    </Button>
                    {editId && (
                        <Button variant="outlined" size="small" onClick={cancelEdit}>Cancel</Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// USERS TAB
// ═══════════════════════════════════════════════════════════════════════════════
const UsersTab = ({ tenantId }) => {
    const [users, setUsers]       = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [form, setForm]         = useState(emptyUser);
    const [editId, setEditId]     = useState(null);
    const [error, setError]       = useState('');
    const [success, setSuccess]   = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        const [u, b] = await Promise.all([
            api(`/tenants/${tenantId}/users`),
            api(`/tenants/${tenantId}/branches`),
        ]);
        setUsers(Array.isArray(u) ? u : []);
        setBranches(Array.isArray(b) ? b : []);
        setLoading(false);
    }, [tenantId]);

    useEffect(() => { load(); }, [load]);

    const roleLabel = (u) => {
        if (u.user_type === 'company_admin') return { label: 'Company Admin', color: '#7B1FA2' };
        if (u.user_type === 'branch_admin')  return { label: 'Branch Admin',  color: '#1565C0' };
        return { label: 'Branch User', color: '#2E7D32' };
    };

    const startEdit = (u) => {
        setEditId(u.id);
        setForm({ name: u.name, email: u.email, password: '', role: u.role, branch_id: u.branch_id || '' });
        setError(''); setSuccess('');
    };

    const cancelEdit = () => { setEditId(null); setForm(emptyUser); setError(''); };

    const handleSave = async () => {
        if (!form.name.trim() || !form.email.trim()) { setError('Name आणि Email आवश्यक आहेत.'); return; }
        if (!editId && !form.password.trim())         { setError('Password आवश्यक आहे.'); return; }
        setSaving(true); setError(''); setSuccess('');
        try {
            const body = { ...form, branch_id: form.branch_id || null };
            if (editId) {
                const res = await api(`/tenants/${tenantId}/users/${editId}`, { method: 'PUT', body });
                if (res.error) { setError(res.error); setSaving(false); return; }
                setSuccess('User update झाला!');
            } else {
                const res = await api(`/tenants/${tenantId}/users`, { method: 'POST', body });
                if (res.error) { setError(res.error); setSaving(false); return; }
                setSuccess('नवीन User तयार झाला!');
            }
            setEditId(null); setForm(emptyUser);
            await load();
        } catch {
            setError('काहीतरी चुकले. पुन्हा प्रयत्न करा.');
        }
        setSaving(false);
    };

    const handleDelete = async (u) => {
        if (!window.confirm(`"${u.name}" user delete करायचा?`)) return;
        setError(''); setSuccess('');
        await api(`/tenants/${tenantId}/users/${u.id}`, { method: 'DELETE' });
        setSuccess('User delete झाला.'); await load();
    };

    return (
        <Box>
            {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Branch</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: 90 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.length === 0 && (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: '#888' }}>कोणताही User नाही.</TableCell></TableRow>
                            )}
                            {users.map(u => {
                                const { label, color } = roleLabel(u);
                                return (
                                    <TableRow key={u.id} hover selected={editId === u.id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 30, height: 30, bgcolor: color, fontSize: '0.75rem' }}>
                                                    {u.name.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight="medium">{u.name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell><Typography variant="caption">{u.email}</Typography></TableCell>
                                        <TableCell>
                                            <Chip label={label} size="small" sx={{ bgcolor: `${color}15`, color, fontWeight: 'bold', fontSize: '0.68rem' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">
                                                {u.branch_name || '— (सर्व Branches)'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Edit"><IconButton size="small" onClick={() => startEdit(u)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(u)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Add / Edit Form */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {editId ? <EditIcon fontSize="small" /> : <PersonAddIcon fontSize="small" />}
                    {editId ? 'User Edit करा' : 'नवीन User जोडा'}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                        label="पूर्ण नाव *" size="small" fullWidth
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    />
                    <TextField
                        label="Email *" size="small" fullWidth
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        disabled={!!editId}
                    />
                    <TextField
                        label={editId ? 'नवीन Password (optional)' : 'Password *'}
                        size="small" fullWidth type="password"
                        value={form.password}
                        onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select value={form.role} label="Role" onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="user">User</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth sx={{ gridColumn: '1 / -1' }}>
                        <InputLabel>Branch</InputLabel>
                        <Select
                            value={form.branch_id}
                            label="Branch"
                            onChange={e => setForm(f => ({ ...f, branch_id: e.target.value }))}
                        >
                            <MenuItem value="">
                                <em>— Company Admin (कोणत्याही branch शी linked नाही)</em>
                            </MenuItem>
                            {branches.map(b => (
                                <MenuItem key={b.id} value={b.id}>
                                    {b.is_main ? '⭐ ' : ''}{b.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#EDE7F6', borderRadius: 1, mb: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                        💡 <b>Branch निवडला नाही + Role = Admin</b> → Company Admin (सर्व branches)<br />
                        💡 <b>Branch निवडला + Role = Admin</b> → Branch Admin (फक्त त्या branch साठी)<br />
                        💡 <b>Branch निवडला + Role = User</b> → Branch User
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained" size="small"
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave} disabled={saving}
                        sx={{ bgcolor: '#102A43', '&:hover': { bgcolor: '#0a1e30' } }}
                    >
                        {editId ? 'Update करा' : 'User जोडा'}
                    </Button>
                    {editId && <Button variant="outlined" size="small" onClick={cancelEdit}>Cancel</Button>}
                </Box>
            </Paper>
        </Box>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DRAWER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const TenantManagementDrawer = ({ tenant, open, onClose }) => {
    const [tab, setTab] = useState(0);

    // Reset tab when a different tenant is opened
    useEffect(() => { if (open) setTab(0); }, [open, tenant?.id]);

    if (!tenant) return null;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', md: 680 }, pt: 0 } }}
        >
            {/* Header */}
            <Box sx={{
                p: 2.5, bgcolor: '#102A43', color: 'white',
                display: 'flex', alignItems: 'center', gap: 2,
                position: 'sticky', top: 0, zIndex: 10,
            }}>
                <AccountBalanceIcon sx={{ fontSize: 32, color: '#FFB300' }} />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold">{tenant.company_name}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        {tenant.id}.{getBaseDomain()} &nbsp;|&nbsp; Plan: {tenant.plan_name} &nbsp;|&nbsp; Max Branches: {tenant.max_branches === -1 ? '∞' : tenant.max_branches}
                    </Typography>

                </Box>
                <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{ px: 2, '& .MuiTab-root': { fontWeight: 'bold', textTransform: 'none', fontSize: '0.95rem' } }}
                >
                    <Tab label="🏢 Branches" />
                    <Tab label="👤 Users" />
                </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ p: 2.5, overflowY: 'auto' }}>
                {tab === 0 && <BranchesTab tenantId={tenant.id} />}
                {tab === 1 && <UsersTab    tenantId={tenant.id} />}
            </Box>
        </Drawer>
    );
};

export default TenantManagementDrawer;
